import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Zap, CheckCircle, Phone, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ElektraContactFormProps {
  defaultService?: string;
  onClose?: () => void;
  className?: string;
}

interface FormData {
  naam: string;
  email: string;
  telefoon: string;
  serviceType: string;
  urgency: string;
  bericht: string;
  address: string;
}

export const ElektraContactForm: React.FC<ElektraContactFormProps> = ({
  defaultService = "",
  onClose,
  className = ""
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    naam: "",
    email: "",
    telefoon: "",
    serviceType: defaultService,
    urgency: "",
    bericht: "",
    address: ""
  });

  const serviceOptions = [
    { value: "groepenkast", label: "Groepenkast Vervangen", icon: "⚡" },
    { value: "led-verlichting", label: "LED Verlichting", icon: "💡" },
    { value: "3-fase", label: "3-Fase Aansluiting", icon: "🔌" },
    { value: "tuinverlichting", label: "Tuinverlichting", icon: "🌙" },
    { value: "renovatie", label: "Elektra Renovatie", icon: "🔧" },
    { value: "smart-home", label: "Smart Home Elektra", icon: "🏠" },
    { value: "spoedstoring", label: "Spoedstoring", icon: "🚨" }
  ];

  const urgencyOptions = [
    { value: "spoedstoring", label: "Spoedstoring (binnen 24u)", color: "text-red-600" },
    { value: "deze-week", label: "Deze week", color: "text-orange-600" },
    { value: "volgende-week", label: "Volgende week", color: "text-yellow-600" },
    { value: "volgende-maand", label: "Volgende maand", color: "text-green-600" }
  ];

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSubmitting(true);

    try {
      // Send via Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('send-elektra-email', {
        body: formData
      });

      if (error) throw error;

      setIsSuccess(true);
      toast.success("Uw elektra aanvraag is succesvol verzonden!");
      
      setTimeout(() => {
        onClose?.();
      }, 2000);

    } catch (error) {
      console.error('Error submitting elektra form:', error);
      toast.error("Er is iets fout gegaan. Probeer het opnieuw of bel ons direct.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <Card className={`mx-auto max-w-md ${className}`}>
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-green-800">Aanvraag Verzonden!</h3>
              <p className="text-sm text-muted-foreground mt-2">
                We nemen binnen 24 uur contact met u op.
                {formData.urgency === 'spoedstoring' && " Voor spoedstoring binnen 1 uur!"}
              </p>
            </div>
            <div className="bg-orange-50 p-3 rounded-lg">
              <p className="text-xs text-orange-800">
                <strong>Direct contact nodig?</strong><br />
                Bel ons: <a href="tel:+31612345678" className="text-orange-600 font-medium">06-12345678</a>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`mx-auto max-w-lg ${className}`}>
      <CardHeader className="text-center">
        <div className="mx-auto w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-2">
          <Zap className="h-6 w-6 text-orange-600" />
        </div>
        <CardTitle className="text-xl text-orange-800">Elektra Aanvraag</CardTitle>
        <p className="text-sm text-muted-foreground">
          Gratis inspectie & advies | NEN-1010 gecertificeerd
        </p>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="naam">Naam *</Label>
              <Input
                id="naam"
                value={formData.naam}
                onChange={(e) => handleInputChange('naam', e.target.value)}
                placeholder="Uw volledige naam"
                required
              />
            </div>
            <div>
              <Label htmlFor="telefoon">Telefoon *</Label>
              <Input
                id="telefoon"
                type="tel"
                value={formData.telefoon}
                onChange={(e) => handleInputChange('telefoon', e.target.value)}
                placeholder="06-12345678"
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="uw.email@voorbeeld.nl"
              required
            />
          </div>

          <div>
            <Label htmlFor="address">Adres (optioneel)</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              placeholder="Straatnaam 123, Plaats"
            />
          </div>

          <div>
            <Label htmlFor="serviceType">Gewenste Service *</Label>
            <Select 
              value={formData.serviceType} 
              onValueChange={(value) => handleInputChange('serviceType', value)}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecteer uw elektra dienst" />
              </SelectTrigger>
              <SelectContent>
                {serviceOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.icon} {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="urgency">Wanneer gewenst? *</Label>
            <Select 
              value={formData.urgency} 
              onValueChange={(value) => handleInputChange('urgency', value)}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecteer timing" />
              </SelectTrigger>
              <SelectContent>
                {urgencyOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <span className={option.color}>{option.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="bericht">Toelichting</Label>
            <Textarea
              id="bericht"
              value={formData.bericht}
              onChange={(e) => handleInputChange('bericht', e.target.value)}
              placeholder="Beschrijf uw wensen of situatie (optioneel)"
              rows={3}
            />
          </div>

          {formData.urgency === 'spoedstoring' && (
            <Alert className="border-red-200 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                <strong>Spoedstoring gemeld!</strong> We nemen binnen 1 uur contact op. 
                Voor direct contact: <a href="tel:+31612345678" className="text-red-600 underline">06-12345678</a>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-3 pt-2">
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="flex-1 bg-orange-600 hover:bg-orange-700"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verzenden...
                </>
              ) : (
                <>
                  <Zap className="mr-2 h-4 w-4" />
                  Gratis Inspectie Aanvragen
                </>
              )}
            </Button>
            
            {onClose && (
              <Button type="button" variant="outline" onClick={onClose}>
                Sluiten
              </Button>
            )}
          </div>

          <div className="text-center pt-2 border-t">
            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
              <span>✅ Gratis inspectie</span>
              <span>⚡ 24/7 spoedservice</span>
              <span>🏆 15+ jaar ervaring</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Direct telefonisch contact: <a href="tel:+31612345678" className="text-orange-600">06-12345678</a>
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};